import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { MarketingShell, DemoStartError } from '../components/Layout'
import { Banner, Card, Chip } from '../ui'
import { api } from '../lib/api'

/**
 * Every claim on this page is sourced from `planning/competitive/wami-parity-scorecard.html`
 * (compiled 20 Aug 2026 from gallabox.com/pricing, docs.gallabox.com, periskope.app/pricing,
 * docs.periskope.app, and Wami's own pricing/onboarding/pairing source) — not invented for
 * this page. Competitor pricing and feature sets change; treat the numbers here as a snapshot,
 * not a live feed, the same caveat the scorecard itself states.
 */
const ALREADY_AHEAD = [
  {
    title: 'An MCP server',
    body: "A real agent-framework connection point — search, reply, open and escalate tickets, react. Neither competitor's own documentation mentions MCP or agent-framework access at all.",
  },
  {
    title: 'Number warmup and ban-avoidance queueing',
    body: 'Actively manages the exact risk an unofficial WhatsApp connection creates. Neither competitor describes anything comparable in their docs.',
  },
  {
    title: 'Four-tier roles',
    body: "Owner/admin/agent/viewer with per-label and per-number grants, against Periskope's flatter model and Gallabox's Enterprise-only custom roles.",
  },
  {
    title: 'An unlimited automation rule engine',
    body: "Nested AND/OR groups, per-action delay and cooldown. Periskope's Pro tier caps rules at 2 per license and sells rule packs; Wami's isn't rule-limited.",
  },
  {
    title: 'Combinable metrics filters',
    body: 'Date range × label × chat type × assignee, matching ALL or ANY — deeper than either competitor describes in their analytics docs.',
  },
  {
    title: 'Industry compliance bundling',
    body: 'Presets that pre-select and lock the right compliance add-ons per vertical, not just a features list.',
  },
]

const CLOSED_THIS_CYCLE = [
  {
    title: 'No-code flow builder',
    body: "Was Gallabox's one structural feature Wami had no equivalent for — automation rules alone can't express a multi-turn guided flow. Wami now has a visual builder alongside the rule engine.",
  },
  {
    title: 'Product catalogue and payment links',
    body: "Closed the gap against Gallabox's WhatsApp Shop for the e-commerce blueprint — a catalogue an agent attaches to a chat, with a generated Stripe or Razorpay payment link. Not a native WhatsApp catalog message; there is no such type reachable through this connection.",
  },
  {
    title: 'Deeper group tooling',
    body: "Group management was Periskope's clearest differentiated territory. Wami's group actions now come with access control that follows the same label/number grants as the inbox, plus a cross-group health rollup no per-group screen offered before.",
  },
]

const OPEN_GAPS = [
  {
    title: 'Public case studies',
    body: 'Both competitors lead their industry story with named customers and real numbers. Wami has none published yet — a real pre-launch gap, not something to paper over with invented testimonials.',
  },
  {
    title: 'Named integrations',
    body: "Gallabox names 20-30+ across CRM, shipping, payments, and marketing. Wami: HubSpot, Zoho CRM, Zoho Desk, Freshdesk, Slack, and Google Sheets, plus Zapier. Periskope: five named plus Zapier.",
  },
  {
    title: 'WhatsApp voice calling',
    body: "Gallabox offers it on Advanced tier and up. Wami and Periskope don't.",
  },
]

const ENTRY_PRICING = [
  { name: 'Gallabox Basic', price: '~$29/mo', note: 'Hard-caps at 3 users, 1 channel, 500 AI credits/mo, 10K broadcast contacts, no drip campaigns.' },
  { name: 'Periskope Starter', price: '$45/mo', note: '$15/seat with the first number free for 3 seats, rising to $25/seat ($75 for 3) under pricing already live on their site.' },
  { name: 'Wami Starter', price: '$49/mo', note: 'Includes 2,000 full conversations — actual conversation capacity, not a metered-credit or broadcast-contact proxy.' },
]

export function Compare() {
  const navigate = useNavigate()
  const startDemo = useMutation({
    mutationFn: api.startDemo,
    onSuccess: () => navigate({ to: '/app' }),
  })

  return (
    <MarketingShell>
      <section className="container section stack gap-4" style={{ paddingBlock: '56px 24px' }}>
        <span className="eyebrow">How Wami compares</span>
        <h1 className="display-small" style={{ maxWidth: '24ch' }}>
          Wami, Gallabox, and Periskope — a fair comparison, not a highlight reel
        </h1>
        <p className="body-large text-muted" style={{ maxWidth: '68ch' }}>
          Reach parity first, then let real advantages speak — that's the standard this page
          holds itself to. Every number below is sourced from public pricing pages and public
          docs, compiled 20 Aug 2026. Prices and feature sets change; check the source before
          you decide, not this page alone.
        </p>
      </section>

      <section className="container" style={{ paddingBottom: '48px' }}>
        <Banner tone="warn">
          <p className="body-medium">
            Wami and Periskope connect the same way — WhatsApp's own multi-device QR pairing, no
            Meta Business Manager or template approval required. Gallabox goes through Meta's
            official Business API. That difference matters most for regulated teams; read the{' '}
            <Link to="/trust">full trust and transport disclosure</Link> before you evaluate
            either unofficial option for a HIPAA or FINRA workflow.
          </p>
        </Banner>
      </section>

      <section className="section rule-top" style={{ background: 'var(--md-surface-container-low)' }}>
        <div className="container stack gap-5">
          <div className="stack gap-2">
            <span className="eyebrow">Entry price, same team</span>
            <h2 className="headline-medium">3 seats, 1 number, billed monthly</h2>
            <p className="body-medium text-muted" style={{ maxWidth: '62ch' }}>
              Not apples-to-apples on what's included — see the note on each. This is the number
              a comparison shopper sees first regardless.
            </p>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px',
            }}
          >
            {ENTRY_PRICING.map((row) => (
              <Card key={row.name} variant={row.name.startsWith('Wami') ? 'filled' : 'outlined'}>
                <div className="stack gap-2">
                  <span className="title-medium">{row.name}</span>
                  <span className="display-small" style={{ fontSize: '2rem' }}>
                    {row.price}
                  </span>
                  <p className="body-small text-muted">{row.note}</p>
                </div>
              </Card>
            ))}
          </div>
          <p className="body-small text-muted">
            Sticker price still lands Wami priciest of the three today — and cheaper than
            Periskope's own announced future pricing. Gallabox figures converted from INR at
            roughly ₹83/$1; regional pricing may differ from what you're served.
          </p>
        </div>
      </section>

      <section className="container section stack gap-5">
        <div className="stack gap-2">
          <span className="eyebrow">Already ahead</span>
          <h2 className="headline-large" style={{ maxWidth: '26ch' }}>
            Real advantages, confirmed against both competitors' own public docs
          </h2>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {ALREADY_AHEAD.map((item) => (
            <Card key={item.title} variant="outlined">
              <div className="stack gap-2">
                <div className="row gap-2 items-baseline">
                  <Chip tone="success">Ahead</Chip>
                  <h3 className="title-medium">{item.title}</h3>
                </div>
                <p className="body-medium text-muted">{item.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="section rule-top" style={{ background: 'var(--md-surface-container-low)' }}>
        <div className="container section stack gap-5">
          <div className="stack gap-2">
            <span className="eyebrow">Closed this cycle</span>
            <h2 className="headline-large" style={{ maxWidth: '30ch' }}>
              Gaps identified in the scorecard, shipped since
            </h2>
          </div>
          <div className="stack gap-3" style={{ maxWidth: '78ch' }}>
            {CLOSED_THIS_CYCLE.map((item) => (
              <Card key={item.title} variant="outlined">
                <div className="stack gap-2">
                  <div className="row gap-2 items-baseline">
                    <Chip tone="assist">Shipped</Chip>
                    <h3 className="title-medium">{item.title}</h3>
                  </div>
                  <p className="body-medium text-muted">{item.body}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="container section stack gap-5">
        <div className="stack gap-2">
          <span className="eyebrow">Still open</span>
          <h2 className="headline-large" style={{ maxWidth: '30ch' }}>
            What we're behind on, stated plainly
          </h2>
          <p className="body-medium text-muted" style={{ maxWidth: '62ch' }}>
            A comparison page that only lists advantages isn't a comparison. These are real,
            unclosed gaps against at least one named competitor.
          </p>
        </div>
        <div className="stack gap-3" style={{ maxWidth: '78ch' }}>
          {OPEN_GAPS.map((item) => (
            <Card key={item.title} variant="outlined">
              <div className="stack gap-2">
                <div className="row gap-2 items-baseline">
                  <Chip tone="warn">Open</Chip>
                  <h3 className="title-medium">{item.title}</h3>
                </div>
                <p className="body-medium text-muted">{item.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="container section stack gap-4 center" style={{ textAlign: 'center', alignItems: 'center' }}>
        <h2 className="headline-large" style={{ maxWidth: '20ch' }}>
          Judge it against your own numbers
        </h2>
        <p className="body-large text-muted" style={{ maxWidth: '52ch' }}>
          No card required. Connect any WhatsApp number and see whether the advantages above hold
          up on your own conversations.
        </p>
        <div className="row gap-3 wrap center">
          <Link to="/signup" className="btn btn-filled btn-large">
            Start 14-day free trial
          </Link>
          <button
            type="button"
            className="btn btn-outlined btn-large"
            onClick={() => startDemo.mutate()}
            disabled={startDemo.isPending}
          >
            {startDemo.isPending ? 'Loading demo…' : 'See a live demo'}
          </button>
        </div>
        {startDemo.isError && <DemoStartError error={startDemo.error} />}
      </section>
    </MarketingShell>
  )
}
